import { useCallback, useMemo } from 'react';
import format from 'date-fns/format';
import { useCalendarContext } from './useCalendar';
import { useBoats } from '../../common/hooks';
import { determineInstabookWarnings, getFirstOfMonth, newTZDate } from '../helpers';
import { CalendarMonthData } from '../types';
import { getDate, getBoatId, isUnavailableEvent, isCalendarEvent } from '../utils/calendar';
import { CALENDAR_DATE_FORMAT_DATE_FNS } from '../constants';

const useInstabookWarning = (
  date: string,
  boatId: string,
  timeToBook: number,
  eventId: number,
) => {
  const { events: months } = useCalendarContext();
  const { boatsIndexedById } = useBoats();

  const isSameMonth = useCallback(
    ({ startDate }: CalendarMonthData) => date && startDate === getFirstOfMonth(date),
    [date],
  );
  const isSameDay = useCallback(
    e => date && getDate(e) === format(newTZDate(date), CALENDAR_DATE_FORMAT_DATE_FNS),
    [date],
  );
  const isSameBoat = useCallback(e => getBoatId(e) === boatId, [boatId]);
  const isSameTrip = useCallback(e => e.id === eventId, [eventId]);

  const dayEventsForBoat = useMemo(() => {
    const monthEvents = months.find(isSameMonth);
    if (!monthEvents) return [];

    let events = monthEvents.events.filter(e => isSameDay(e) && isSameBoat(e));

    events = monthEvents.events.filter(
      e => isSameDay(e) && isSameBoat(e)
        && (isSameTrip(e) || isUnavailableEvent(e) || isCalendarEvent(e)),
    );

    return events;
  }, [months, isSameMonth, isSameDay, isSameBoat, isSameTrip]);

  if (!boatId) {
    return undefined;
  }
  const boat = boatsIndexedById[boatId];
  return determineInstabookWarnings(
    boat,
    date,
    dayEventsForBoat,
    timeToBook,
  );
};

export default useInstabookWarning;
